import React from "react";
import RentTaxCalculator from "./RentTaxCalculator";
import BlogTOC from "./BlogTOC";
import { useParams } from "react-router-dom";
import { blogPosts } from "./blogData";
import "./blog.css";

import NeighborhoodMatchmaker from "./NeighborhoodMatchmaker";
import FurnishedROICalculator from "../components/FurnishedROICalculator";
import FlatScorecard from "../components/FlatScorecard";
import RentalYieldCalculator from "../components/RentalYieldCalculator";
import KabuliyatnamaChecklist from "../components/KabuliyatnamaChecklist";
import AdUnit from "../components/AdUnit";

const ADSENSE_SLOT_BLOG_INTRO = process.env.REACT_APP_ADSENSE_SLOT_BLOG_INTRO;

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  if (!post) return <div>Post not found.</div>;
  // Insert calculator only for the Ghar Bahal Kar post
  const isGharBahalKar = post.slug === "legal-tax-ghar-bahal-kar-nepal-2026";
  const isExpatGuide = post.slug === "kathmandu-expat-executive-housing-guide";
  const isRentalYieldReport =
    post.slug === "kathmandu-rental-yield-report-2026";
  const isFurnishedPremium =
    post.slug === "furnished-premium-lock-and-leave-apartments-nepal";
  const isFlatScorecard =
    post.slug === "kathmandu-flat-hunting-checklist-water-monsoon-power";
  const isKabuliyatnama =
    post.slug === "bulletproof-kabuliyatnama-nepal-rental-agreement";
  let contentHtml = post.content;
  // For Ghar Bahal Kar, inject calculator
  if (isGharBahalKar) {
    contentHtml = contentHtml.replace(
      /(<h2[^>]*>Fines, Penalties, and Early Bird Discounts<\/h2>)/i,
      `<div id='rent-tax-calc-react'></div>$1`,
    );
  }

  // Split every post into its intro (everything before the first <h2>) and the
  // rest of the article, so a single, restrained ad slot can sit right after the
  // intro on every post without needing per-post markup. All the calculator/tool
  // injection points below live well after the first <h2>, so they operate safely
  // on `restHtml`.
  const firstH2Index = contentHtml.search(/<h2/i);
  const introHtml =
    firstH2Index > -1 ? contentHtml.slice(0, firstH2Index) : contentHtml;
  const restHtml = firstH2Index > -1 ? contentHtml.slice(firstH2Index) : "";

  // For Expat Guide, split for matchmaker
  let beforeMatchmaker = null,
    afterMatchmaker = null;
  if (isExpatGuide) {
    [beforeMatchmaker, afterMatchmaker] = restHtml.split(
      '<div id="neighborhood-matchmaker-react"></div>',
    );
  }
  // For Furnished Premium, split for ROI calculator
  let beforeROICalc = null,
    afterROICalc = null;
  if (isFurnishedPremium) {
    [beforeROICalc, afterROICalc] = restHtml.split(
      '<div id="furnished-roi-calculator-react"></div>',
    );
  }
  // For Rental Yield report, split for yield calculator
  let beforeYieldCalc = null,
    afterYieldCalc = null;
  if (isRentalYieldReport) {
    [beforeYieldCalc, afterYieldCalc] = restHtml.split(
      '<div id="rental-yield-calculator-react"></div>',
    );
  }
  // For Flat Scorecard, split for scorecard
  let beforeScorecard = null,
    afterScorecard = null;
  if (isFlatScorecard) {
    [beforeScorecard, afterScorecard] = restHtml.split(
      '<div id="flat-scorecard-react"></div>',
    );
  }
  // For Kabuliyatnama post, split for checklist
  let beforeKabuChecklist = null,
    afterKabuChecklist = null;
  if (isKabuliyatnama) {
    [beforeKabuChecklist, afterKabuChecklist] = restHtml.split(
      '<div id="kabuliyatnama-checklist-react"></div>',
    );
  }

  return (
    <div
      className="blog-post"
      style={{
        maxWidth: 800,
        margin: "2.5rem auto",
        background: "var(--paper)",
        borderRadius: "16px",
        boxShadow: "var(--shadow-md)",
        padding: "2.5rem 1.5rem",
        color: "var(--ink-900)",
      }}
    >
      {/* Author/date info and TOC for Ghar Bahal Kar post */}
      {isGharBahalKar && (
        <>
          <div
            style={{
              color: "var(--ink-700)",
              fontSize: "0.98rem",
              marginBottom: 8,
            }}
          >
            <b>By {post.author}</b> | Published: {post.date} | Pillar:{" "}
            {post.pillar}
          </div>
          <BlogTOC />
        </>
      )}
      <div className="blog-article">
        <span dangerouslySetInnerHTML={{ __html: introHtml }} />
        <AdUnit slot={ADSENSE_SLOT_BLOG_INTRO} />
        {isGharBahalKar ? (
          <>
            {/* Render content up to calculator placeholder */}
            {restHtml.split("<div id='rent-tax-calc-react'></div>")[0] && (
              <span
                dangerouslySetInnerHTML={{
                  __html: restHtml.split(
                    "<div id='rent-tax-calc-react'></div>",
                  )[0],
                }}
              />
            )}
            <RentTaxCalculator />
            {/* Render rest of content */}
            {restHtml.split("<div id='rent-tax-calc-react'></div>")[1] && (
              <span
                dangerouslySetInnerHTML={{
                  __html: restHtml.split(
                    "<div id='rent-tax-calc-react'></div>",
                  )[1],
                }}
              />
            )}
          </>
        ) : isExpatGuide ? (
          <>
            <span dangerouslySetInnerHTML={{ __html: beforeMatchmaker }} />
            <NeighborhoodMatchmaker />
            <span dangerouslySetInnerHTML={{ __html: afterMatchmaker }} />
          </>
        ) : isRentalYieldReport ? (
          <>
            <span dangerouslySetInnerHTML={{ __html: beforeYieldCalc }} />
            <RentalYieldCalculator />
            <span dangerouslySetInnerHTML={{ __html: afterYieldCalc }} />
          </>
        ) : isFurnishedPremium ? (
          <>
            <span dangerouslySetInnerHTML={{ __html: beforeROICalc }} />
            <FurnishedROICalculator />
            <span dangerouslySetInnerHTML={{ __html: afterROICalc }} />
          </>
        ) : isFlatScorecard ? (
          <>
            <span dangerouslySetInnerHTML={{ __html: beforeScorecard }} />
            <FlatScorecard />
            <span dangerouslySetInnerHTML={{ __html: afterScorecard }} />
          </>
        ) : isKabuliyatnama ? (
          <>
            <span dangerouslySetInnerHTML={{ __html: beforeKabuChecklist }} />
            <KabuliyatnamaChecklist />
            <span dangerouslySetInnerHTML={{ __html: afterKabuChecklist }} />
          </>
        ) : (
          <span dangerouslySetInnerHTML={{ __html: restHtml }} />
        )}
      </div>
      {/* SEO meta tags */}
      <meta name="description" content={post.metaDescription} />
      <title>{post.title} | Rental Highway Blog</title>
      {/* FAQPage Schema for SEO rich snippets */}
      {isGharBahalKar && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Do I have to pay rent tax if my property is vacant?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. House rent tax is an income tax. If your property is empty and generating zero income, you do not owe rent tax for those months. However, you still owe your standard annual property tax.",
                },
              },
              {
                "@type": "Question",
                name: "Can I deduct maintenance or repair costs from my rental income?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "For individual landlords paying the local ward office, the answer is usually no. The 10% is calculated on your gross rental income. Corporate landlords filing with the IRD, however, can deduct valid business expenses before calculating corporate tax.",
                },
              },
              {
                "@type": "Question",
                name: "What happens if my tenant subleases the property?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "If your primary tenant subleases a room or floor to someone else, the primary tenant is acting as a landlord for that specific transaction. The primary tenant is responsible for paying the tax on the sublease income they receive.",
                },
              },
            ],
          })}
        </script>
      )}
    </div>
  );
}
